import { useMemo } from "react";
import { Line } from "react-chartjs-2";
import type { ChartData, ChartOptions, ScriptableContext } from "chart.js";
import { useChartTheme } from "@/lib/chartTheme";
import { registerCharts } from "./register";
import { withAlpha } from "./utils";

registerCharts();

interface SparklineProps {
  data: number[];
  /** "positive" trends colour green, "negative" red, "neutral" uses accent. */
  trend?: "positive" | "negative" | "neutral";
  className?: string;
  height?: number;
  /** Delay (ms) before the entry animation starts. */
  delay?: number;
}

/** Tiny inline line chart for KPI cards — no axes, no legend, no tooltip. */
export function Sparkline({ data, trend = "neutral", className, height = 40, delay = 0 }: SparklineProps) {
  const { tokens } = useChartTheme();

  const colour =
    trend === "positive" ? tokens.success :
    trend === "negative" ? tokens.danger  :
    tokens.chartFocal;

  const chartData = useMemo<ChartData<"line">>(() => {
    return {
      labels: data.map((_, i) => i),
      datasets: [
        {
          data,
          borderColor: colour,
          backgroundColor: (ctx: ScriptableContext<"line">) => {
            const { chart } = ctx;
            const { ctx: cv, chartArea } = chart;
            if (!chartArea) return withAlpha(colour, 0.15);
            const g = cv.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
            g.addColorStop(0, withAlpha(colour, 0.32));
            g.addColorStop(1, withAlpha(colour, 0));
            return g;
          },
          borderWidth: 1.75,
          tension: 0.4,
          pointRadius: 0,
          pointHoverRadius: 0,
          fill: true,
        },
      ],
    };
  }, [data, colour]);

  const options: ChartOptions<"line"> = {
    responsive: true,
    maintainAspectRatio: false,
    animation: { duration: 800, easing: "easeOutQuart", delay },
    interaction: { mode: "nearest", intersect: false },
    plugins: { legend: { display: false }, tooltip: { enabled: false } },
    scales: { x: { display: false }, y: { display: false } },
    elements: { line: { borderJoinStyle: "round", borderCapStyle: "round" } },
  };

  return (
    <div className={className} style={{ height }}>
      <Line data={chartData} options={options} />
    </div>
  );
}
